"use client"
import { useMemo } from "react"
import {
  PLAN_KEYS,
  defaultGraphData,
} from "../../../extrapolated-graph/graphConstants"
import { formatSpeedupHoverText } from "../../../extrapolated-graph/graphUtils"
import { getCapabilityStageLabelForPlanValue } from "../../../extrapolated-graph/capabilityThresholds"
import {
  currentLevelRiskAt,
  fmtLikelihood,
} from "../../../extrapolated-graph/takeoverRiskLikelihoodData"
import { type PlanId } from "@/lib/branchTypes"
import { useHudContext, useHudDates } from "../../shared/playbookHUDInfra"

// The capability line's numbers AT THE STORY DATE (not a hovered year): the
// R&D speedup the line plots, the agent level that value has reached, and
// the current-level takeover risk — the same three figures the combined
// graph hover derives from its hovered point (CombinedCapabilityRiskDetail),
// interpolated from the same data the graph draws
// (extrapolatedGraphData.json: the shared past plus the active plan's line).
// Feeds the tab-74 stat-row readouts (RdSpeedupStat / TakeoverRiskStat) and
// the labor hover's agent-level table.
export type CurrentCapabilityStats = {
  speedupValue: number
  // "2.1x" — the graph hover's own formatting (formatSpeedupHoverText).
  speedupText: string
  // "Superhuman AI Researcher" — matches CAPABILITY_LEVEL_LADDER labels.
  levelLabel: string
  // Current-level risk at the story date (currentLevelRiskAt), "5%" formatted.
  risk: number | null
  riskText: string | null
}

export const useCurrentCapabilityStats = (): CurrentCapabilityStats | null => {
  const { targetDate } = useHudDates()
  const { activePlan } = useHudContext()
  const targetMs = targetDate.getTime()
  return useMemo(() => {
    const series = [
      ...defaultGraphData.shared,
      ...defaultGraphData[PLAN_KEYS[activePlan]] ?? [],
    ]
      .map((datum) => ({ timestamp: Date.parse(datum.date), value: datum.value }))
      .filter((point) => Number.isFinite(point.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp)
    if (series.length === 0) return null
    // Linear interpolation at the story date, clamped to the series' ends.
    const first = series[0]
    const last = series[series.length - 1]
    let value: number
    if (targetMs <= first.timestamp) value = first.value
    else if (targetMs >= last.timestamp) value = last.value
    else {
      value = last.value
      for (let index = 1; index < series.length; index++) {
        const prev = series[index - 1]
        const next = series[index]
        if (targetMs > next.timestamp) continue
        value =
          next.timestamp === prev.timestamp
            ? next.value
            : prev.value +
              ((next.value - prev.value) * (targetMs - prev.timestamp)) /
                (next.timestamp - prev.timestamp)
        break
      }
    }
    // The story date as a fractional year — the risk table's x-axis.
    const yearStart = Date.UTC(targetDate.getUTCFullYear(), 0, 1)
    const nextYearStart = Date.UTC(targetDate.getUTCFullYear() + 1, 0, 1)
    const fractionalYear =
      targetDate.getUTCFullYear() +
      (targetMs - yearStart) / (nextYearStart - yearStart)
    const risk = currentLevelRiskAt(value, activePlan, fractionalYear)
    return {
      speedupValue: value,
      speedupText: formatSpeedupHoverText(value),
      levelLabel: getCapabilityStageLabelForPlanValue(value, activePlan),
      risk,
      riskText: risk === null ? null : fmtLikelihood(risk),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs, activePlan])
}

// The agent level the line reaches at an arbitrary (fractional) YEAR — the
// zone-columns level list tracks the cursor's year with it while the FIELD
// is hovered (the line zone reads its hovered point's label directly).
// Same series + interpolation as the hook above, keyed by year instead of
// the story date.
export const capabilityLevelLabelAtYear = (
  yearFraction: number,
  activePlan: PlanId,
): string => {
  const wholeYear = Math.floor(yearFraction)
  const yearStart = Date.UTC(wholeYear, 0, 1)
  const nextYearStart = Date.UTC(wholeYear + 1, 0, 1)
  const targetMs = yearStart + (yearFraction - wholeYear) * (nextYearStart - yearStart)
  const series = [
    ...defaultGraphData.shared,
    ...defaultGraphData[PLAN_KEYS[activePlan]] ?? [],
  ]
    .map((datum) => ({ timestamp: Date.parse(datum.date), value: datum.value }))
    .filter((point) => Number.isFinite(point.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp)
  if (series.length === 0) return ""
  const first = series[0]
  const last = series[series.length - 1]
  let value: number
  if (targetMs <= first.timestamp) value = first.value
  else if (targetMs >= last.timestamp) value = last.value
  else {
    value = last.value
    for (let index = 1; index < series.length; index++) {
      const prev = series[index - 1]
      const next = series[index]
      if (targetMs > next.timestamp) continue
      value =
        next.timestamp === prev.timestamp
          ? next.value
          : prev.value +
            ((next.value - prev.value) * (targetMs - prev.timestamp)) /
              (next.timestamp - prev.timestamp)
      break
    }
  }
  return getCapabilityStageLabelForPlanValue(value, activePlan)
}
